import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Observable } from 'rxjs';

import { GeneralConstants } from 'app/constants/general-constants';
import { Store } from 'app/model';
import { makeSeamPattern, SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { FilterData } from 'app/model/user-data/filter-data';
import { messagingService } from 'app/services/messaging.service';
import { searchService } from 'app/services/search-service';
import { RootState } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';

import { PatternForm } from './pattern-form';
import { selectMidFilters } from '../../tuples/tuple.slice';
import { saveNewPattern, selectPattern, selectSelectedPattern } from '../slice';

interface PatternEditorProps extends RouteComponentProps<{ id: string; pattern: string }> {
    request: SeamRequest;
    patterns: SeamPattern[];
    opposingId?: string;

    // redux props
    formData?: ModelWithoutId<SeamPattern>;
    filterData: FilterData;

    // redux actions
    selectPattern: (pattern?: ModelWithoutId<SeamPattern>) => void;
    saveNewPattern: (pattern: ModelWithoutId<SeamPattern>, request: SeamRequest) => Observable<SeamPattern>;
    store: Store | undefined;
}

class PatternEditor extends React.Component<PatternEditorProps> {
    public componentDidMount() {
        this.syncPattern();
    }

    public componentDidUpdate() {
        this.syncPattern();
    }

    public render(): React.ReactNode {
        if (!this.props.formData) {
            // still loading
            return GeneralConstants.RENDER_NOTHING;
        }

        const pattern = this.props.formData;

        return (
            <React.Fragment>
                <PatternForm
                    data={pattern}
                    correction={this.props.request.correction}
                    targetStoreId={this.props.request.store.id}
                    seamRequest={this.props.request}
                    initialStats={this.props.request.initialStats}
                    onChange={(data) => this.props.selectPattern(data)}
                    onSubmit={(createNew) => this.submitNewPattern(pattern, createNew)}
                    onCancel={() => this.backToPatterns()}
                    onClone={() => this.clone(pattern)}
                    onSearch={(opts, searchAfter, skipFilters, ignoreDeactivated) =>
                        searchService.findRemaining(
                            this.props.request,
                            {
                                pattern: pattern.id,
                                correction: this.props.request.correction ? '1' : '0',
                                storeId: this.props.request.store.id,
                                type: pattern.type,
                                opposingId: pattern.opposingId,
                                searchParams: {
                                    ...pattern.data,
                                },
                                filters: skipFilters
                                    ? []
                                    : this.props.filterData.filters.filter((filter) => '' !== filter.value),
                                searchAfter,
                                ignoreDeactivated,
                            },
                            opts,
                        )
                    }
                    store={this.props.store}
                />
            </React.Fragment>
        );
    }

    private syncPattern(): void {
        const patId = 'new' === this.props.match.params.pattern ? undefined : this.props.match.params.pattern;

        if (this.props.formData && this.props.formData.id === patId) {
            // we are in sync
            return;
        }

        if (!patId) {
            this.props.selectPattern(makeSeamPattern());

            return;
        }

        const pattern = this.props.patterns.find((pat) => pat.id === patId);
        if (pattern) {
            this.props.selectPattern(pattern);
        }
    }

    private submitNewPattern(data: ModelWithoutId<SeamPattern>, createNew: boolean) {
        this.props.saveNewPattern(data, this.props.request).subscribe((saved) => {
            this.props.selectPattern(saved);
            messagingService.showSuccess('Pattern Saved');

            if (data.noReplacement) {
                this.props.history.push(`/seam/request/${this.props.request.id}/patterns`);

                return;
            }
            if (saved.id !== data.id) {
                this.props.history.push(`/seam/request/${this.props.request.id}/patterns/${saved.id}`);
            }

            if (createNew) {
                this.props.selectPattern(makeSeamPattern());
                this.props.history.push(`/seam/request/${this.props.request.id}/patterns/new`);

                return;
            }
        });
    }

    private backToPatterns() {
        this.props.history.push(`/seam/request/${this.props.request.id}/patterns`);
    }

    private clone(data: ModelWithoutId<SeamPattern>) {
        this.props.selectPattern({ ...data, id: undefined });
        this.props.history.push(`/seam/request/${this.props.request.id}/patterns/new`);
    }
}

const mapStateToProps = (state: RootState) => ({
    formData: selectSelectedPattern(state),
    filterData: selectMidFilters(state),
});

const thunks = {
    selectPattern: selectPattern as ActionCreatorWithPayload<ModelWithoutId<SeamPattern> | undefined>,
    saveNewPattern,
};
const connected = connect(mapStateToProps, thunks)(withRouter(PatternEditor));

export { connected as PatternEditor };
